/**
 * Fixed thread palettes used by formats that store palette indices instead of RGB values.
 * Colour tables from EmbroidePy/pyembroidery (MIT License, © Tatarize and contributors).
 */

export interface PaletteThread {
  rgb: number;
  name: string;
  code: string;
}

const t = (r: number, g: number, b: number, name: string, code: string): PaletteThread => ({ rgb: (r << 16) | (g << 8) | b, name, code });
const h = (rgb: number, name: string, code: string): PaletteThread => ({ rgb, name, code });

/** Brother PEC palette. Index 0 is unused ("unknown"). */
export const PEC_THREADS: (PaletteThread | null)[] = [
  null,
  t(14, 31, 124, 'Prussian Blue', '1'), t(10, 85, 163, 'Blue', '2'), t(0, 135, 119, 'Teal Green', '3'),
  t(75, 107, 175, 'Cornflower Blue', '4'), t(237, 23, 31, 'Red', '5'), t(209, 92, 0, 'Reddish Brown', '6'),
  t(145, 54, 151, 'Magenta', '7'), t(228, 154, 203, 'Light Lilac', '8'), t(145, 95, 172, 'Lilac', '9'),
  t(158, 214, 125, 'Mint Green', '10'), t(232, 169, 0, 'Deep Gold', '11'), t(254, 186, 53, 'Orange', '12'),
  t(255, 255, 0, 'Yellow', '13'), t(112, 188, 31, 'Lime Green', '14'), t(186, 152, 0, 'Brass', '15'),
  t(168, 168, 168, 'Silver', '16'), t(125, 111, 0, 'Russet Brown', '17'), t(255, 255, 179, 'Cream Brown', '18'),
  t(79, 85, 86, 'Pewter', '19'), t(0, 0, 0, 'Black', '20'), t(11, 61, 145, 'Ultramarine', '21'),
  t(119, 1, 118, 'Royal Purple', '22'), t(41, 49, 51, 'Dark Gray', '23'), t(42, 19, 1, 'Dark Brown', '24'),
  t(246, 74, 138, 'Deep Rose', '25'), t(178, 118, 36, 'Light Brown', '26'), t(252, 187, 197, 'Salmon Pink', '27'),
  t(254, 55, 15, 'Vermilion', '28'), t(240, 240, 240, 'White', '29'), t(106, 28, 138, 'Violet', '30'),
  t(168, 221, 196, 'Seacrest', '31'), t(37, 132, 187, 'Sky Blue', '32'), t(254, 179, 67, 'Pumpkin', '33'),
  t(255, 243, 107, 'Cream Yellow', '34'), t(208, 166, 96, 'Khaki', '35'), t(209, 84, 0, 'Clay Brown', '36'),
  t(102, 186, 73, 'Leaf Green', '37'), t(19, 74, 70, 'Peacock Blue', '38'), t(135, 135, 135, 'Gray', '39'),
  t(216, 204, 198, 'Warm Gray', '40'), t(67, 86, 7, 'Dark Olive', '41'), t(253, 217, 222, 'Flesh Pink', '42'),
  t(249, 147, 188, 'Pink', '43'), t(0, 56, 34, 'Deep Green', '44'), t(178, 175, 212, 'Lavender', '45'),
  t(104, 106, 176, 'Wisteria Violet', '46'), t(239, 227, 185, 'Beige', '47'), t(247, 56, 102, 'Carmine', '48'),
  t(181, 75, 100, 'Amber Red', '49'), t(19, 43, 26, 'Olive Green', '50'), t(199, 1, 86, 'Dark Fuchsia', '51'),
  t(254, 158, 50, 'Tangerine', '52'), t(168, 222, 235, 'Light Blue', '53'), t(0, 103, 62, 'Emerald Green', '54'),
  t(78, 41, 144, 'Purple', '55'), t(47, 126, 32, 'Moss Green', '56'), t(255, 204, 204, 'Flesh Pink', '57'),
  t(255, 217, 17, 'Harvest Gold', '58'), t(9, 91, 166, 'Electric Blue', '59'), t(240, 249, 112, 'Lemon Yellow', '60'),
  t(227, 243, 91, 'Fresh Green', '61'), t(255, 153, 0, 'Orange', '62'), t(255, 240, 141, 'Cream Yellow', '63'),
  t(255, 200, 200, 'Applique', '64'),
];

/** Janome JEF palette. Index 0 is a placeholder. */
export const JEF_THREADS: (PaletteThread | null)[] = [
  null,
  h(0x000000, 'Black', '002'), h(0xffffff, 'White', '001'), h(0xffff17, 'Yellow', '204'), h(0xff6600, 'Orange', '203'),
  h(0x2f5933, 'Olive Green', '219'), h(0x237336, 'Green', '226'), h(0x65c2c8, 'Sky', '217'), h(0xab5a96, 'Purple', '208'),
  h(0xf669a0, 'Pink', '201'), h(0xff0000, 'Red', '225'), h(0xb1704e, 'Brown', '214'), h(0x0b2f84, 'Blue', '207'),
  h(0xe4c35d, 'Gold', '003'), h(0x481a05, 'Dark Brown', '205'), h(0xac9cc7, 'Pale Violet', '209'),
  h(0xfcf294, 'Pale Yellow', '210'), h(0xf999b7, 'Pale Pink', '211'), h(0xfab381, 'Peach', '212'), h(0xc9a480, 'Beige', '213'),
  h(0x970533, 'Wine Red', '215'), h(0xa0b8cc, 'Pale Sky', '216'), h(0x7fc21c, 'Yellow Green', '218'),
  h(0xe5e5e5, 'Silver Gray', '220'), h(0x889b9b, 'Gray', '221'), h(0x98d6bd, 'Pale Aqua', '227'), h(0xb2e1e3, 'Baby Blue', '228'),
  h(0x368ba0, 'Powder Blue', '229'), h(0x4f83ab, 'Bright Blue', '230'), h(0x386a91, 'Slate Blue', '231'),
  h(0x071650, 'Navy Blue', '232'), h(0xf999a2, 'Salmon Pink', '233'), h(0xf9676b, 'Coral', '234'),
  h(0xe3311f, 'Burnt Orange', '235'), h(0xe2a188, 'Cinnamon', '236'), h(0xb59474, 'Umber', '237'), h(0xe4cf99, 'Blond', '238'),
  h(0xffcb00, 'Sunflower', '239'), h(0xe1add4, 'Orchid Pink', '240'), h(0xc3007e, 'Peony Purple', '241'),
  h(0x80004b, 'Burgundy', '242'), h(0x540571, 'Royal Purple', '243'), h(0xb10525, 'Cardinal Red', '244'),
  h(0xcae0c0, 'Opal Green', '245'), h(0x899856, 'Moss Green', '246'), h(0x5c941a, 'Meadow Green', '247'),
  h(0x003114, 'Dark Green', '248'), h(0x5dae94, 'Aquamarine', '249'), h(0x4cbf8f, 'Emerald Green', '250'),
  h(0x007772, 'Peacock Green', '251'), h(0x595b61, 'Dark Gray', '252'), h(0xfffff2, 'Ivory White', '253'),
  h(0xb15818, 'Hazel', '254'), h(0xcb8a07, 'Toast', '255'), h(0x986c80, 'Salmon', '256'), h(0x98692d, 'Cocoa Brown', '257'),
  h(0x4d3419, 'Sienna', '258'), h(0x4c330b, 'Sepia', '259'), h(0x33200a, 'Dark Sepia', '260'), h(0x523a97, 'Violet Blue', '261'),
  h(0x0d217e, 'Blue Ink', '262'), h(0x1e77ac, 'Sola Blue', '263'), h(0xb2dd53, 'Green Dust', '264'), h(0xf33689, 'Crimson', '265'),
  h(0xde649e, 'Floral Pink', '266'), h(0x984161, 'Wine', '267'), h(0x4c5612, 'Olive Drab', '268'), h(0x4c881f, 'Meadow', '269'),
  h(0xe4de79, 'Mustard', '270'), h(0xcb8a1a, 'Yellow Ocher', '271'), h(0xcba21c, 'Old Gold', '272'),
  h(0xff9805, 'Honey Dew', '273'), h(0xfcb257, 'Tangerine', '274'), h(0xffe505, 'Canary Yellow', '275'),
  h(0xf0331f, 'Vermilion', '202'), h(0x1a842d, 'Bright Green', '206'), h(0x386cae, 'Ocean Blue', '222'),
  h(0xe3c4b4, 'Beige Gray', '223'), h(0xe3ac81, 'Bamboo', '224'),
];

export const hexToRgb = (hex: string): number => parseInt(hex.replace('#', '').slice(0, 6), 16) || 0;
export const rgbToHex = (rgb: number): string => '#' + (rgb & 0xffffff).toString(16).padStart(6, '0');

/** "Red-mean" weighted RGB distance (compuphase), as used by common embroidery tools. */
export function colorDistance(a: number, b: number): number {
  const r1 = (a >> 16) & 0xff, g1 = (a >> 8) & 0xff, b1 = a & 0xff;
  const r2 = (b >> 16) & 0xff, g2 = (b >> 8) & 0xff, b2 = b & 0xff;
  const rm = Math.round((r1 + r2) / 2);
  const r = r1 - r2, g = g1 - g2, bl = b1 - b2;
  return (((512 + rm) * r * r) >> 8) + 4 * g * g + (((767 - rm) * bl * bl) >> 8);
}

/** Index of the nearest palette colour; ties choose the later entry. Null entries are skipped. */
export function nearestIndex(rgb: number, palette: (PaletteThread | null)[]): number {
  let best = -1;
  let bestD = Infinity;
  palette.forEach((p, i) => {
    if (!p) return;
    const d = colorDistance(rgb, p.rgb);
    if (d <= bestD) {
      bestD = d;
      best = i;
    }
  });
  return best;
}

/**
 * Maps each thread to a palette index such that distinct colours get distinct palette entries
 * where possible (each unique colour reserves its nearest remaining palette entry).
 */
export function uniquePaletteIndices(rgbs: number[], palette: (PaletteThread | null)[]): number[] {
  const available = palette.slice();
  const chart: (PaletteThread | null)[] = palette.map(() => null);
  const unique = [...new Set(rgbs)];
  for (const rgb of unique) {
    const idx = nearestIndex(rgb, available);
    if (idx < 0) break;
    available[idx] = null;
    chart[idx] = { rgb, name: '', code: '' };
  }
  return rgbs.map((rgb) => nearestIndex(rgb, chart));
}
